import { Skeleton } from '@/components/ui/skeleton';
import { getCategoryMeta } from '@/components/transactions/screen/categoryIndex';
import { useCategories } from '@/hooks/queries/useCategories';
import type { useSpaceBudget } from '@/hooks/queries/useSpaceBudget';
import { useCategoryIndex } from '@/hooks/useCategoryIndex';
import { isBudget } from '@/lib/spaces/budget';
import { spaceCategorySpending, type SpaceCategorySpend } from '@/lib/spaces/categories';
import { HOME_CATEGORIES, readFailed } from '@/lib/spaces/home';
import type { SpaceDetail } from '@/types/spaces';
import { SettlementCard, SettlementFailure } from './SettlementParts';
import { SpaceCategorySpendRow } from './SpaceBudgetParts';

const TITLE = 'Categorias';

export function SpaceHomeCategories({ space, budget, onBudget }: Readonly<{ space: SpaceDetail; budget: ReturnType<typeof useSpaceBudget>; onBudget: () => void }>) {
  const tree = useCategories();
  const categoryIndex = useCategoryIndex();
  const action = { label: 'Orçamento', onClick: onBudget };
  if (readFailed(budget, isBudget)) return <SettlementCard title={TITLE} action={action}><SettlementFailure message="Não foi possível carregar as categorias do espaço." retry={budget.mutate} /></SettlementCard>;
  if (!budget.data) return <SettlementCard title={TITLE} action={action}><div aria-busy="true" aria-label="Carregando categorias" className="space-y-3"><Skeleton className="h-9 rounded-xl" /><Skeleton className="h-9 rounded-xl" /><Skeleton className="h-9 rounded-xl" /></div></SettlementCard>;
  // Without the taxonomy the rows still read, by their ids; the budget sheet offers the retry for the names.
  const rows = spaceCategorySpending(budget.data, tree.data);
  if (!rows.length) return <SettlementCard title={TITLE} action={action}><p className="text-sm text-text-secondary">{space.kind === 'trip' ? 'Nada entrou na viagem ainda.' : 'Nada entrou no espaço neste mês ainda.'}</p></SettlementCard>;
  const shown = rows.slice(0, HOME_CATEGORIES);
  const more = rows.length - shown.length;
  const renderRow = (row: SpaceCategorySpend) => <li key={row.categoryId}><SpaceCategorySpendRow row={row} meta={getCategoryMeta(categoryIndex, row.categoryId)} /></li>;
  return <SettlementCard title={TITLE} action={action}>
    <ul aria-label="Categorias do espaço" className="space-y-3">{shown.map(renderRow)}</ul>
    <MoreCategories count={more} onBudget={onBudget} />
  </SettlementCard>;
}

function MoreCategories({ count, onBudget }: Readonly<{ count: number; onBudget: () => void }>) {
  if (count <= 0) return null;
  return <button type="button" onClick={onBudget} className="cursor-pointer text-xs font-medium text-text-secondary underline-offset-4 hover:text-text-primary hover:underline">
    Mais {count.toLocaleString('pt-BR')} {count === 1 ? 'categoria' : 'categorias'}
  </button>;
}
